import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";

export type QuestionnaireStandardUsage = QuestionnairePayload["usageType"];

const STANDARD_USAGE: QuestionnaireStandardUsage[] = [
  "triage",
  "preConsultation",
  "postConsultation",
  "attendance",
];

/**
 * Tipos de uso (FHIR / domínio) permitidos ao enviar questionários a partir da fila da agenda,
 * conforme status do Appointment e sinal opcional de atendimento em andamento.
 *
 * Pré-consulta: apenas `booked` / `arrived` (confirmado na fila).
 * `pending`: só triagem (e pós-consulta se `hasActiveEncounter`).
 * Fallback documentado para PR: sem `hasActiveEncounter`, pós-consulta e acompanhamento na fila
 * só entram com `fulfilled`. `hasActiveEncounter` (ex.: ClinicalRegisterContext.inProgress === id)
 * libera pós-consulta durante o atendimento.
 */
export function getAllowedUsageTypesForScheduleQuestionnaireSend(
  appointmentStatus: string | undefined,
  options?: { hasActiveEncounter?: boolean },
): QuestionnaireStandardUsage[] {
  const hasEncounter = options?.hasActiveEncounter === true;
  if (!appointmentStatus || appointmentStatus === "cancelled") {
    return [];
  }
  if (appointmentStatus === "proposed") {
    return ["triage"];
  }
  if (appointmentStatus === "fulfilled") {
    return ["postConsultation", "attendance"];
  }
  if (appointmentStatus === "pending") {
    const list: QuestionnaireStandardUsage[] = ["triage"];
    if (hasEncounter) {
      list.push("postConsultation");
    }
    return list;
  }
  if (appointmentStatus === "booked" || appointmentStatus === "arrived") {
    const list: QuestionnaireStandardUsage[] = ["preConsultation"];
    if (hasEncounter) {
      list.push("postConsultation");
    }
    return list;
  }
  return ["preConsultation"];
}

export function isQuestionnaireAllowedForScheduleSend(
  questionnaire: QuestionnairePayload,
  allowed: QuestionnaireStandardUsage[],
): boolean {
  if (allowed.length === 0) return false;
  if (!STANDARD_USAGE.includes(questionnaire.usageType)) {
    return false;
  }
  return allowed.includes(questionnaire.usageType);
}
